import { useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import firebase from 'src/firebase';
import VideoPlayer from 'src/components/VideoPlayer';
import './styles.css';
import { FIREBASE_ACTIONS, differ } from './helpers';
import QRCard from './QRCard';
import { useSnackbar } from 'notistack';
import { DEFAULT_ERROR, SESSION_STATES, WEB_PULSE_INTERVAL } from 'src/utils/constants';
import CircularLoader from 'src/components/Loaders/CircularLoader';
import ReactPlayer from 'react-player';

const Watch = props => {
  const { enqueueSnackbar } = useSnackbar();

  const uid = useRef(uuidv4());
  const { search } = useLocation();
  const locale = useMemo(() => {
    const q = new URLSearchParams(search);
    return (q && q.get('hl')) || null;
  }, [search]);

  // Utility refs
  const playerRef = useRef();
  const intervalRef = useRef();

  // Reducer to handle all updates
  const [sessionData, setSessionData] = useState({ _id: uid.current });
  const [loading, setLoading] = useState(false);

  const handleReload = useCallback((e, disconnect_type) => {
    const newId = uuidv4();
    uid.current = newId;
    if(intervalRef.current){
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if(disconnect_type === 'web'){
      FIREBASE_ACTIONS.disconnect(sessionData._id);
    }
    setSessionData({ _id: newId });
  },[sessionData._id]);

  const handleError = useCallback((err) => {
    console.log(err);
    enqueueSnackbar(DEFAULT_ERROR, {variant: 'error'});
    FIREBASE_ACTIONS.errorDetected(sessionData._id).then(() => {
      handleReload();
    });

  }, [enqueueSnackbar, handleReload, sessionData._id]);

  useEffect(() => {
    // MAIN DB LISTENER
    const unsubscribe = firebase.firestore().doc(`sessions/${sessionData._id}`).onSnapshot((doc) => {
      const nextData = doc.data();
      if(!nextData) return;
      setSessionData(prev => differ(nextData, prev));
    }, handleError);

    console.log(sessionData._id);
    // Window close/reload event
    function disconnectWeb(){ handleReload(null, 'web') };
    window.addEventListener('beforeunload', disconnectWeb);

    return () => {
      window.removeEventListener('beforeunload', disconnectWeb);
      unsubscribe();
    }

  },[handleError, handleReload, sessionData._id]);

  useEffect(() => {
    if(!sessionData || !sessionData.url) return;
    if(sessionData.disconnect) { handleReload(null, 'remote'); return;} // show QR screen
    if(!ReactPlayer.canPlay(sessionData.url)) {handleError(); return;}

    if(!!sessionData.url && !sessionData.state){
      // Initiate session
      console.log("Initiating....");
      setLoading(true);
      FIREBASE_ACTIONS.initiateSession(sessionData._id).then(() => {
        console.log("Initiated....");
        setLoading(false);
      }).catch(handleError);
    } else {
      // Stuff that needs to updating on UI for the state change

      // Seek functionality
      const { seek_to, seeking } = sessionData || {};
      if((!seeking  && (seek_to || seek_to === 0)) && !!playerRef.current){
        console.log("Seeking...");
        setSessionData(prev => ({...prev, seeking: true }));
        playerRef.current.seekTo(seek_to, 'seconds');
        FIREBASE_ACTIONS.updateSeek(sessionData._id, seek_to).then(() => {
          console.log("Seeked to:", seek_to);
          setSessionData(prev => ({ ...prev, seek_to: null, seeking: false }));
        }).catch(handleError);
      }

      // Web Pulse Function
      if(!intervalRef.current && !!sessionData.url){
        const id = setInterval(() => {
          console.log('Pulsed...');
          FIREBASE_ACTIONS.webPulse(sessionData._id).catch(handleError);
        }, WEB_PULSE_INTERVAL);
        intervalRef.current = id;
      }
    }

  }, [handleError, handleReload, sessionData]);

  const handleProgress = useCallback(async({ playedSeconds }) => {
    if(sessionData.state !== 'playing') return;
    const newTs = parseInt(playedSeconds);
    // Update pulse in a single write with ts when state = playing
    FIREBASE_ACTIONS.updateTs(sessionData._id, newTs).catch(handleError);
  },[handleError, sessionData._id, sessionData.state]);

  const handlePlay = useCallback(() => {
    console.log('Playing.....');
    if(sessionData.state !== SESSION_STATES.paused && sessionData.state !== SESSION_STATES.ready) return; // change is already detected
    FIREBASE_ACTIONS.controlPlayback(sessionData._id, SESSION_STATES.playing).catch(handleError);
  }, [handleError, sessionData]);

  const handlePause = useCallback(() => {
    console.log('Pausing.....');
    if(sessionData.state !== SESSION_STATES.playing) return; // change is already detected or some other state was there
    FIREBASE_ACTIONS.controlPlayback(sessionData._id, SESSION_STATES.paused).catch(handleError);
  }, [handleError, sessionData]);

  const handleReady = useCallback(() => {
    // Start video from some seek value if provided
      const { seek } = sessionData || {};
      if(seek?.sec) playerRef.current.seekTo(seek.sec, 'seconds');
      const videoPlayer = playerRef.current.getInternalPlayer();
      if(!videoPlayer) {
        // Unlikely case but handled - after ready if cannot get video player instance
        handleError();
        return ;
      }
      const promise = videoPlayer.play();
      if(promise !== undefined){
        promise.then(_ => {
          FIREBASE_ACTIONS.controlPlayback(sessionData._id, SESSION_STATES.playing).catch(handleError);
        }).catch(error => {
          // Autoplay was prevented.
          FIREBASE_ACTIONS.controlPlayback(sessionData._id, SESSION_STATES.ready).catch(handleError);
        });
      }
  }, [handleError, sessionData]);

  const handleEnd = () => {
    FIREBASE_ACTIONS.donePlaying(sessionData._id).then(() => {
      handleReload();
    }).catch(handleError);
  };

  // SHOW QR SCREEN
  if(!sessionData?.url && !sessionData.state){
    return (
      <div className='qr-page'>
        <QRCard value={`__FTBD__:${sessionData._id}`} handleRefresh={handleReload} locale={locale} />
      </div>
    );
  }

  if(loading || (!!sessionData.url && !sessionData.state)){
    return (
      <div className='qr-page'>
        <CircularLoader />
      </div>
    )
  }

  // LOAD VIDEO PLAYER
  return (
    <VideoPlayer
      ref={playerRef} 
      url={sessionData.url || ''}
      onProgress={handleProgress}
      onPlay={handlePlay}
      onPause={handlePause}
      onReady={handleReady}
      onEnded={handleEnd}
      onClose={(e) => handleReload(e, 'web')}
      title={sessionData.title || ''}
      playing={sessionData.state === SESSION_STATES.playing}
    />
  )
};

export default Watch;
